import { useEffect, type DragEvent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components'
import { rem } from 'polished'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import zIndex from 'styles/tools/z-index'
import { useAddChatFiles } from './hooks/useAddChatFiles'
import { hasDraggedFiles } from './chatFiles'

const DROP_ICON = <LinePaperclipIcon size="L" />

const DropArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  block-size: 100%;
  min-block-size: 0;
  min-inline-size: 0;
`

const DropOverlay = styled.div`
  position: absolute;
  inset: ${rem(8)};
  z-index: ${zIndex.tooltip};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-items: center;
  justify-content: center;
  border: ${rem(2)} dashed ${props => props.theme.palette.highlight.primary.normalForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  pointer-events: none;
`

// Stopping propagation skips react-dropzone's own handler, which would otherwise cancel the
// browser's default and so block text and link drops into the fields inside the area.
function ignoreNonFileDrag(event: DragEvent<HTMLElement>) {
  if (!hasDraggedFiles(event.dataTransfer)) {
    event.stopPropagation()
  }
}

// Keeps a file that misses the area from opening in the tab, while text drops elsewhere still land.
function preventStrayFileDrop(event: globalThis.DragEvent) {
  if (hasDraggedFiles(event.dataTransfer)) {
    event.preventDefault()
  }
}

const ROOT_PROPS = {
  onDragEnter: ignoreNonFileDrag,
  onDragOver: ignoreNonFileDrag,
  onDragLeave: ignoreNonFileDrag,
  onDrop: ignoreNonFileDrag,
}

interface Props {
  files: Array<File>
  onFilesChange: (files: Array<File>) => void
  disabled?: boolean
  children: ReactNode
}

/**
 * Lets files dragged anywhere over its children join the chat message. Dropped files go
 * through the same checks as picked and pasted ones; drags of text or links are left to
 * the field they land on. While disabled, dropped files are ignored.
 */
function ChatFileDropArea({ files, onFilesChange, disabled = false, children }: Props) {
  const intl = useIntl()
  const addFiles = useAddChatFiles(files, onFilesChange)

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    disabled,
    noClick: true,
    noKeyboard: true,
    preventDropOnDocument: false,
  })

  useEffect(() => {
    document.addEventListener('dragover', preventStrayFileDrop)
    document.addEventListener('drop', preventStrayFileDrop)
    return () => {
      document.removeEventListener('dragover', preventStrayFileDrop)
      document.removeEventListener('drop', preventStrayFileDrop)
    }
  }, [])

  return (
    <DropArea {...getRootProps(ROOT_PROPS)}>
      {children}
      {isDragActive && (
        <DropOverlay aria-hidden>
          {DROP_ICON}
          <BodyText variant="large" weight="bold" colour="inherit">
            {intl.formatMessage({
              id: '0DvvgXL0izHdq93mm_k3',
              defaultMessage: 'Drop photos or files to attach',
              description: 'Shown over the TripPlanner chat while the traveller drags files over it, before they drop them onto the message',
            })}
          </BodyText>
        </DropOverlay>
      )}
    </DropArea>
  )
}

export default ChatFileDropArea
